import React, { useContext, useEffect, useState } from "react";
import classNames from "~/utils/classNames";
import Loader from "~/components/utils/Loader";
import { Link } from "react-router-dom";
import ButtonCount from "./ButtonCount";
import { UIContext } from "../../contexts/ui-context";
import { isMobile } from "react-device-detect";
import renderInlineHtml from "~/utils/renderInlineHtml";

export const buttonLoaderSizes = {
  "extra-small": 12,
  small: 14,
  base: 18,
  large: 20,
};

export const loaderColors = {
  primary: "#fff",
  default: "#333",
  transparent: "#333",
  dashed: "#616161",
  success: "#09514F",
  danger: "#6D0707",
  "danger-light": "#B91C1C",
  dark: "#fff",
  pro: "#fff",
  template: "#333",
};

/**
 * Flexible button component that can render as a button, link, or div depending on props.
 * Supports loading states, icons, confirmation dialogs, and various styling options.
 *
 * @component
 * @example
 * // Basic button
 * <Button label="Click me" onClick={handleClick} />
 *
 * // Button with icon and loading state
 * <Button
 *   label="Save"
 *   icon={SaveIcon}
 *   loading={isLoading}
 *   style="primary"
 * />
 *
 * // Link button
 * <Button label="Go to page" to="/dashboard" />
 *
 * @param {Object} props - Component properties
 * @param {string} [props.label] - Button text content
 * @param {number} [props.count] - Number to display in a badge next to the button text
 * @param {string} [props.type] - HTML button type attribute (e.g., "submit", "button")
 * @param {Function} [props.onClick] - Click handler function
 * @param {string} [props.confirm] - Confirmation message to show before executing onClick
 * @param {string} [props.to] - React Router path for Link component rendering
 * @param {string} [props.href] - URL for anchor tag rendering (external or internal links)
 * @param {('extra-small'|'small'|'base'|'large')} [props.size='base'] - Button size variant
 * @param {('default'|'primary'|'transparent'|'dashed'|'success'|'danger'|'danger-light'|'dark'|'pro'|'template')} [props.style='default'] - Button style variant. `dashed` is the empty-slot CTA: an action that fills something not yet filled. `pro` is the outlined button for the `.bg-pro` field.
 * @param {React.ComponentType|React.ReactElement} [props.icon] - Icon component or React element (e.g., <span>) to display in the button
 * @param {('left'|'right')} [props.iconPosition='left'] - Position of the icon relative to text
 * @param {boolean} [props.rounded=false] - Whether to apply rounded button styles
 * @param {string} [props.className=''] - Additional CSS classes to apply
 * @param {boolean} [props.loading=false] - Whether to display loading spinner
 * @param {boolean} [props.loadingOnClick=false] - Whether to set loading state automatically on click
 * @param {boolean} [props.readOnly=false] - Whether to render as unclickable div instead of button
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.mobileIconOnly=false] - Whether to show only icon on mobile devices
 * @param {React.ReactNode} [props.children] - Additional content to render inside the button
 * @returns {React.ReactElement} The rendered button component
 */
export default function Button(props) {
  const {
    label,
    type = null,
    count,
    onClick = () => {},
    confirm = null,
    to = null,
    href = null,
    size = "base",
    style = "default",
    icon: Icon = null,
    iconPosition = "left",
    rounded = false,
    className = "",
    loading = false,
    loadingOnClick = false,
    readOnly = false,
    disabled = false,
    mobileIconOnly = false,
    children,
    ...passthroughProps
  } = props;

  const [localLoading, setLocalLoading] = useState(loading);
  useEffect(() => {
    setLocalLoading(loading);
  }, [loading]);

  const fullClassName = classNames(
    "button",
    disabled && "disabled",
    rounded ? "!rounded-full" : null,
    `button-${size}`,
    `button-${style}`,
    Icon || localLoading || loading || count
      ? "!inline-flex items-center justify-center"
      : null,
    localLoading || loading ? "loading" : null,
    className,
  );

  const iconOnly = isMobile && mobileIconOnly;

  const { showPrompt } = useContext(UIContext);

  const handleClick = (e) => {
    if (loadingOnClick) setLocalLoading(true);
    if (onClick && !disabled && !loading) {
      if (confirm) {
        showPrompt(confirm, () => onClick(e));
        return;
      }
      onClick(e);
    }
  };

  const handleLinkClick = (e) => {
    if (loadingOnClick) setLocalLoading(true);
    if (confirm) {
      e.preventDefault();
      showPrompt(confirm, () => {
        window.location.href = e.target.href;
      });
    }
  };

  const content = () => (
    <>
      {(localLoading || loading) && (
        <Loader
          stroke={loaderColors[style]}
          width={buttonLoaderSizes[size]}
          strokeWidth={8}
        />
      )}
      {!(localLoading || loading) &&
        Icon &&
        iconPosition == "left" &&
        (React.isValidElement(Icon) ? (
          Icon
        ) : (
          <Icon className="button-icon" aria-hidden="true" />
        ))}
      {!iconOnly && renderInlineHtml(label)}
      {!iconOnly && children}
      {!iconOnly && count > 0 && <ButtonCount count={count} />}
      {!(localLoading || loading) &&
        Icon &&
        iconPosition == "right" &&
        (React.isValidElement(Icon) ? (
          Icon
        ) : (
          <Icon className="button-icon" aria-hidden="true" />
        ))}
    </>
  );

  if (readOnly) {
    return (
      <div
        className={classNames(fullClassName, "cursor-pointer")}
        role="button"
        aria-label={iconOnly && label ? label : passthroughProps["aria-label"]}
        {...passthroughProps}
      >
        {content()}
      </div>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={fullClassName}
        onClick={handleClick}
        aria-label={iconOnly && label ? label : passthroughProps["aria-label"]}
        {...passthroughProps}
      >
        {content()}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={"block " + fullClassName}
        target={props.target || href[0] == "/" ? "_self" : "_blank"}
        onClick={handleLinkClick}
        aria-label={iconOnly && label ? label : passthroughProps["aria-label"]}
        {...passthroughProps}
      >
        {content()}
      </a>
    );
  }

  return (
    <button
      type={type || "button"}
      className={fullClassName}
      onClick={handleClick}
      disabled={disabled}
      aria-label={iconOnly && label ? label : passthroughProps["aria-label"]}
      {...passthroughProps}
    >
      {content()}
    </button>
  );
}
